/**
 * Rotating member QR payload helpers (§6a.1).
 *
 * Live QR: `mem:{membership_id}:{HMAC-SHA256(qr_secret, floor(now_ms / 30_000))}`
 *
 * The DB stores `qr_secret` as a base64-encoded 32-byte value. The client
 * fetches the secret once (via getMembershipQrSecret) and rotates the payload
 * locally every 30 seconds — works offline at flaky-WiFi gates.
 *
 * IMPORTANT: This module is environment-neutral (no node imports, no DOM
 * imports). It uses the Web Crypto subtle API, which is available in the
 * browser, in Node 16+, and in Edge runtimes. Do not import it from a
 * "server-only" file: it works in client components too.
 */

const WINDOW_SECONDS = 30;
const WINDOW_MS = WINDOW_SECONDS * 1000;

function getCrypto(): Crypto {
  // Web Crypto is globally available on modern Node and the browser.
  if (typeof globalThis.crypto?.subtle !== 'undefined') {
    return globalThis.crypto;
  }
  throw new Error('Web Crypto API is not available in this environment');
}

function toBytes(value: string): ArrayBuffer {
  // Spread into a fresh ArrayBuffer so the resulting view is typed
  // as Uint8Array<ArrayBuffer> rather than Uint8Array<ArrayBufferLike>,
  // which the Web Crypto signatures require.
  const encoded = new TextEncoder().encode(value);
  const buffer = new ArrayBuffer(encoded.byteLength);
  new Uint8Array(buffer).set(encoded);
  return buffer;
}

function bytesToHex(bytes: ArrayBuffer): string {
  const view = new Uint8Array(bytes);
  let out = '';
  for (let i = 0; i < view.length; i += 1) {
    out += view[i]!.toString(16).padStart(2, '0');
  }
  return out;
}

/**
 * Computes HMAC-SHA256 of the current 30-second window using the supplied
 * secret. The DB function `extensions.hmac` uses the secret bytes directly
 * (i.e. the base64 string is fed as raw key material). We mirror that here.
 */
async function computeHmacHex(
  qrSecret: string,
  windowIndex: number,
): Promise<string> {
  const crypto = getCrypto();
  const key = await crypto.subtle.importKey(
    'raw',
    toBytes(qrSecret),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  );
  const signature = await crypto.subtle.sign(
    'HMAC',
    key,
    toBytes(windowIndex.toString()),
  );
  return bytesToHex(signature);
}

/**
 * Returns the current 30-second window index, derived from the supplied
 * timestamp in milliseconds (defaults to Date.now()).
 */
export function currentQrWindow(nowMs: number = Date.now()): number {
  return Math.floor(nowMs / WINDOW_MS);
}

/**
 * Returns the milliseconds until the next 30-second rotation. Use this to
 * drive a countdown ring on the QR card.
 */
export function msUntilNextWindow(nowMs: number = Date.now()): number {
  return WINDOW_MS - (nowMs % WINDOW_MS);
}

/**
 * Computes the live member-QR payload. Safe to call from both server and
 * client: the secret is the only sensitive input, and both contexts already
 * hold it (server: just-fetched; client: cached).
 */
export async function computeMemberQrPayload(
  membershipId: string,
  qrSecret: string,
  nowMs: number = Date.now(),
): Promise<string> {
  const windowIndex = currentQrWindow(nowMs);
  const hmacHex = await computeHmacHex(qrSecret, windowIndex);
  return `mem:${membershipId}:${hmacHex}`;
}

export const MEMBER_QR_WINDOW_SECONDS = WINDOW_SECONDS;
export const MEMBER_QR_WINDOW_MS = WINDOW_MS;
